import type { IErrorApiResponse, ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseQueryOptions,
  isEmptyValue,
  restrictApi,
  serialize,
  useQuery,
} from '@wyz/utils';
import { omitBy, orderBy } from 'lodash-es';
import { useApi, useConfig } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import {
  IDimensionParameterQuery,
  IDimensionParameterResult,
  ISeason,
} from '~/purchase/types';
import { CommonQueryKeys } from '~/common/api';

type VehicleTypeGroup = {
  codename: string;
  types: Array<number>;
};
type Options = {
  getDimensions?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IDimensionParameterResult>>,
      IErrorApiResponse
    >
  > & {
    args: IDimensionParameterQuery & {
      customerId: string;
    };
  };
  getVehicleTypes?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<VehicleTypeGroup>>,
      IErrorApiResponse
    >
  >;
  getSeasons?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<Array<ISeason>>, IErrorApiResponse>
  >;
};

export const useManageParameters = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const getDimensionsQuery = useQuery({
    queryKey: PurchaseQueryKeys.parameters.dimension(
      options.getDimensions?.args.customerId ?? '',
      options.getDimensions?.args,
    ),
    queryFn: () => {
      return callCoreApi<ISuccessApiResponse<Array<IDimensionParameterResult>>>(
        `/tenants/api/wtb/tenants/${tenantCode}/customers/${options.getDimensions?.args.customerId ?? ''}/tyres/parameters/dimensions?${serialize(
          omitBy(
            {
              'vehicle-types': options.getDimensions?.args.vehicleTypes,
            },
            isEmptyValue,
          ),
        )}`,
      );
    },
    refetchOnWindowFocus: false,
    gcTime: Infinity,
    staleTime: Infinity,
    ...options.getDimensions,
    enabled: Boolean(
      options.getDimensions?.args &&
      options.getDimensions.args.customerId &&
      options.getDimensions?.args.vehicleTypes.length > 0,
    ),
  });

  const getSeasonsQuery = useQuery({
    queryKey: CommonQueryKeys.parameters.seasons(),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<ISeason>>>(
        `/offers/api/catalog/parameters/seasons`,
      ).then((response) => {
        // it's unsafe to rely on publickKey (uuid), or on name "Été", "Hiver"..., and according to Data team, id is stable
        const ORDER = [1, 3, 2]; // Summer, All-Season, Winter

        return {
          ...response,
          data: orderBy(
            response.data,
            [
              (season) => {
                const idx = ORDER.indexOf(season.id);
                return idx === -1 ? Infinity : idx;
              },
            ],
            ['asc'],
          ),
        };
      }),
    refetchOnWindowFocus: false,
    gcTime: Infinity,
    staleTime: Infinity,
    enabled: false,
    ...options.getSeasons,
  });

  return restrictApi(options, {
    getDimensions: getDimensionsQuery,
    getSeasons: getSeasonsQuery,
  });
};
